import { useRef } from "react";
import SEO from "../components/SEO/SEO";
import { organizationLd, websiteLd } from "../seo/jsonLd";
import ColorInit from "../helpers/ColorInit";
import Header from "../components/Header/Header";
import Banner from "../layout/Banner/Banner";
import FeatureOne from "../layout/Feature/FeatureOne";
import Footer from "../components/Footer/Footer";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { BrandsScroll } from "../components/HomePageBrands/BrandsScroll";

// Import our new unified product component
import { UnifiedProducts } from "../components/Products";
import {
  useHeaderPinnedProducts,
  useLatestProducts,
  useBestSellerProducts,
  type Product,
} from "../hooks/products";
import { addProductToCart } from "../hooks/useSupabase";
import { useCartStore } from "../store/cartStore";
import toast from "react-hot-toast";
import { WishlistProvider } from "../hooks/useWishlistContext";
import Promotions from "../components/homePageProduct/Promotions";
import TrustFactors from "../components/homePageProduct/TrustFactors";
// import Newsletter from "../components/homePageProduct/Newsletter";
import FlashDeals from "../components/homePageProduct/FlashDeals";
import { useFlashDeals } from "../hooks/useFlashDeals";

const HomePage = () => {
  const { data: flashDeals = [] } = useFlashDeals();

  const { products: storeFrontProducts } = useHeaderPinnedProducts();
  const { products: latestProducts, isLoading: latestLoading, error: latestError } = useLatestProducts(8);
  const { products: bestSellerProducts, isLoading: bestSellerLoading, error: bestSellerError } = useBestSellerProducts(8);
  // Distribute header pinned products across banner + promotion slots
  const bannerProducts = storeFrontProducts.slice(0, 5);
  const promotionOneProducts = storeFrontProducts.slice(5, 8);
  const promotionTwoProducts = storeFrontProducts.slice(8, 9);
  const promotionThreeProducts = storeFrontProducts.slice(9, 11);

  const addToCartStore = useCartStore((s) => s.addToCart);

  const dbSyncTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const handleAddToCart = (product: Product) => {
    if ((product.quantity ?? 0) <= 0) return;

    const cartItem = useCartStore.getState().items.find(
      (i) => i.product_id === product.id
    );
    const alreadyInCart = cartItem?.quantity ?? 0;
    if (alreadyInCart >= (product.quantity ?? 0)) {
      toast.error("You already have the maximum available quantity in your cart.", { id: "cart-max-qty" });
      return;
    }

    addToCartStore(product as any, 1);

    const existing = dbSyncTimers.current.get(product.id);
    if (existing) clearTimeout(existing);
    dbSyncTimers.current.set(product.id, setTimeout(async () => {
      const finalQty = useCartStore.getState().items.find(
        (i) => i.product_id === product.id
      )?.quantity ?? 1;
      try {
        await addProductToCart({ productId: product.id, quantity: finalQty });
      } catch {
        toast.error("Failed to add to cart. Please try again.");
      } finally {
        dbSyncTimers.current.delete(product.id);
      }
    }, 300));
  };

  const handleQuickView = (_product: Product) => {};

  const handleViewAll = (_type: string) => {};

  return (
    <>
      <SEO
        title="My Perfume — Luxury Perfumes & Fragrances"
        description="Shop luxury perfumes and fragrances at My Perfume. Discover signature scents, oud, and gift sets with worldwide shipping."
        canonicalPath="/"
        jsonLd={[organizationLd(), websiteLd()]}
      />
      {/* Custom ScrollToTop Button */}
      <ScrollToTopButton />

      {/* ColorInit */}
      <ColorInit color={false} />

      {/* HeaderOne */}
      <Header />

      {/* BannerOne - Dynamic if data exists, else falls back to default inside component */}
      <Banner variant="one" products={bannerProducts} />

      {/* Brands Infinite Scroll Section */}
      <BrandsScroll />

      {/* Main Categories Feature Section */}
      <FeatureOne />

      {/* Wrap product sections with WishlistProvider */}
      <WishlistProvider>
        {/* Flash Deals Section - Above Featured Products */}
        <FlashDeals
          deals={flashDeals}
          onAddToCart={(p) =>
            addProductToCart({ productId: p.id, quantity: 1 })
          }
        />

        {/* Featured Products Section */}
        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          <UnifiedProducts
            sortType="featured"
            limitCount={8}
            title="Featured Products"
            subtitle="Our most Featured products"
            onAddToCart={handleAddToCart}
            onQuickView={handleQuickView}
            onViewAll={() => handleViewAll("featured")}
          />
        </div>
        {promotionOneProducts.length > 0 && (
          <div className="py-8">
            <Promotions variant="one" products={promotionOneProducts} />
          </div>
        )}

        {/* Latest Products Section */}
        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          <UnifiedProducts
            sortType="newest"
            limitCount={8}
            title="Latest Products"
            subtitle="Check out our newest arrivals"
            products={latestProducts}
            isLoading={latestLoading}
            error={latestError}
            onAddToCart={handleAddToCart}
            onQuickView={handleQuickView}
            onViewAll={() => handleViewAll("recent")}
          />
        </div>

        {promotionTwoProducts.length > 0 && (
          <div className="py-8 px-4">
            <Promotions variant="two" products={promotionTwoProducts} />
          </div>
        )}

        {/* <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          <UnifiedProducts
            sortType="on_sale"
            limitCount={8}
            title="On Sale Now"
            subtitle="Don't miss these amazing deals"
            products={onSaleProducts}
            isLoading={onSaleLoading}
            onAddToCart={handleAddToCart}
            onQuickView={handleQuickView}
            onViewAll={() => handleViewAll("on_sale")}
          />
        </div> */}

        {promotionThreeProducts.length > 0 && (
          <div className="py-8">
            <Promotions variant="three" products={promotionThreeProducts} />
          </div>
        )}

        {/* Best Sellers Section */}
        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          <UnifiedProducts
            sortType="popular"
            limitCount={8}
            title="Best Sellers"
            subtitle="Our most popular products"
            products={bestSellerProducts}
            isLoading={bestSellerLoading}
            error={bestSellerError}
            onAddToCart={handleAddToCart}
            onQuickView={handleQuickView}
            onViewAll={() => handleViewAll("popular")}
          />
        </div>
      </WishlistProvider>

      <TrustFactors />

      {/* <Newsletter /> */}

      <Footer />
    </>
  );
};

export default HomePage;
